import React, { useEffect } from 'react';
import { X, Download, ExternalLink, FileText, AlertCircle } from 'lucide-react';

const PdfModal = ({ isOpen, onClose, pdfUrl, title, description }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !pdfUrl) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="pdf-modal-title"
    >
      <div 
        className="relative flex flex-col w-full max-w-5xl h-[92vh] max-h-[900px] bg-white rounded-2xl shadow-2xl overflow-hidden border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-emerald-950 text-white border-b border-emerald-900 flex-shrink-0">
          <div className="flex items-center space-x-3 overflow-hidden pr-4">
            <div className="p-2 bg-emerald-800/80 rounded-lg text-emerald-300 flex-shrink-0">
              <FileText className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 id="pdf-modal-title" className="text-base sm:text-lg font-bold text-white truncate">
                {title || 'Dokument'}
              </h3>
              {description && (
                <p className="text-xs text-emerald-200/80 truncate hidden sm:block">
                  {description}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2 flex-shrink-0">
            <a
              href={pdfUrl}
              download
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs sm:text-sm font-semibold rounded-lg transition-colors shadow-sm"
              title="Download PDF til din enhed"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Download PDF</span>
            </a>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-900 hover:bg-emerald-800 text-emerald-200 hover:text-white text-xs sm:text-sm font-medium rounded-lg transition-colors"
              title="Åbn i nyt faneblad"
            >
              <ExternalLink className="w-4 h-4" />
              <span className="hidden md:inline">Åbn i ny fane</span>
            </a>
            <button
              onClick={onClose}
              className="p-1.5 text-emerald-200 hover:text-white hover:bg-emerald-900 rounded-lg transition-colors"
              aria-label="Luk modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* PDF Viewer Body */}
        <div className="relative flex-1 bg-zinc-100 overflow-hidden">
          <iframe
            src={`${pdfUrl}#toolbar=1&navpanes=0`}
            title={title || 'PDF visning'}
            className="w-full h-full border-0"
          />
          
          {/* Fallback hint for browsers / mobile Safari where iframes may need direct tap */}
          <div className="md:hidden absolute bottom-3 left-3 right-3 p-3 bg-white/95 backdrop-blur-md rounded-xl shadow-lg border border-zinc-200 flex items-center justify-between text-xs text-zinc-700 pointer-events-auto">
            <span className="flex items-center gap-1.5 font-medium">
              <AlertCircle className="w-4 h-4 text-emerald-700 flex-shrink-0" />
              Driller visningen på din mobil?
            </span>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-emerald-800 hover:underline flex items-center gap-1 flex-shrink-0"
            >
              Åbn direkte <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfModal;
